import { Button, ButtonProps } from '@mui/material';
import React, { MouseEventHandler, useEffect, useState, useRef } from 'react';

import {
  getLocalStorage,
  LocalStorageKeys as LSKeys,
  removeLocalStorage,
  setLocalStorage,
} from '../../utils/localStorage';

const BACKOFF_START = 15000;
const BACKOFF_MAX = 600000;
const EXPIRED_BACKOFF = 15 * 60 * 1000;

const isExpiredBackoff = () => {
  const backoffEnd = Number(getLocalStorage(LSKeys.BACKOFF_END));
  const now = Date.now();
  return backoffEnd && now - backoffEnd > EXPIRED_BACKOFF;
};

type Props = Omit<ButtonProps, 'children' | 'onClick'> & {
  onClick: MouseEventHandler<HTMLButtonElement>;
  buttonText: string;
  inactiveButtonText: string;
};

const BackoffButton: React.FC<Props> = ({ disabled, onClick, buttonText, inactiveButtonText, ...rest }: Props) => {
  const [inactive, setInactive] = useState(true);
  const [timeLeft, setTimeLeft] = useState(0);
  const intervalId = useRef<NodeJS.Timeout>();

  const startBackoff = () => {
    let backoffDuration = Number(getLocalStorage(LSKeys.BACKOFF_DURATION));
    if (backoffDuration === 0) {
      // no local storage result; set initial backoffDuration
      backoffDuration = BACKOFF_START;
    } else if (backoffDuration >= BACKOFF_MAX) {
      // limit to max backoffDuration
      backoffDuration = BACKOFF_MAX;
    } else {
      // increment backoff
      backoffDuration *= 2;
    }

    const backoffEnd = Date.now() + backoffDuration;

    setLocalStorage(LSKeys.BACKOFF_DURATION, backoffDuration.toString());
    setLocalStorage(LSKeys.BACKOFF_END, backoffEnd.toString());

    setTimeLeft(backoffDuration);
  };

  const retrieveBackoff = () => {
    const backoffEnd = Number(getLocalStorage(LSKeys.BACKOFF_END));
    const now = Date.now();

    if (isExpiredBackoff()) {
      removeLocalStorage(LSKeys.BACKOFF_DURATION);
      removeLocalStorage(LSKeys.BACKOFF_END);
    } else if (now < backoffEnd) {
      setTimeLeft(backoffEnd - now);
    } else {
      // backoff is not expired, keep backoffDuration but remove backoffEnd
      removeLocalStorage(LSKeys.BACKOFF_END);
    }
  };

  useEffect(() => {
    // this useEffect is responsible to setting the button inactive state based on the timeLeft,
    // as well as updating the timeLeft state through the setInterval callback
    if (inactive === false && timeLeft > 0) {
      setInactive(true);
      if (intervalId.current) {
        clearInterval(intervalId.current);
        intervalId.current = undefined;
      }

      intervalId.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev - 1000 > 0) {
            return prev - 1000;
          } else {
            return 0;
          }
        });
      }, 1000);
    }

    if (timeLeft === 0) {
      setInactive(false);
    }
  }, [timeLeft, inactive]);

  useEffect(() => {
    // on component mount, we check if there is a backoff in progress
    retrieveBackoff();

    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
        intervalId.current = undefined;
      }
    };
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick(event);
    startBackoff();
  };

  return (
    <Button {...rest} disabled={disabled || inactive} onClick={handleClick}>
      {!inactive && <>{buttonText}</>}
      {inactive && <>{`${inactiveButtonText} in ${Math.ceil(timeLeft / 1000)}...`}</>}
    </Button>
  );
};

export { BackoffButton };
export type { Props as BackoffButtonProps };
